import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtRp  = n => 'Rp ' + (n || 0).toLocaleString('id-ID');
const fmtDT  = d => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const fmtDur = (start, end) => {
  const ms = (end ? new Date(end) : new Date()) - new Date(start);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}j ${m}m`;
};

const S = {
  card:  { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' },
  label: { fontSize: '.68rem', fontFamily: "'DM Mono',monospace", letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 3 },
  inp:   { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '.88rem', background: '#FAFAFA', outline: 'none' },
  btn:   (c = '#CD5226') => ({ padding: '9px 18px', borderRadius: 8, background: c, color: c === '#CD5226' ? '#fff' : 'white', border: 'none', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', transition: 'opacity .15s' }),
};

// ─── Modal Buka Shift ─────────────────────────────────────────────────────────
function BukaShiftModal({ users, onClose, onSaved }) {
  const [openedBy, setOpenedBy] = useState('');
  const [cash, setCash]         = useState('');
  const [staff, setStaff]       = useState([]);
  const [saving, setSaving]     = useState(false);

  const toggleStaff = id => setStaff(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  async function buka() {
    if (!openedBy) return alert('Pilih yang membuka shift');
    setSaving(true);
    const opener = users.find(u => u.id === openedBy);
    const { data: shift, error } = await supabase.from('shifts').insert({
      opened_by: openedBy,
      opened_by_name: opener?.name || '',
      opening_cash: parseInt(cash) || 0,
      status: 'open',
    }).select().single();

    if (error) { alert('Gagal membuka shift: ' + error.message); setSaving(false); return; }

    // Insert staff yang hadir
    const staffRows = staff.map(uid => {
      const u = users.find(x => x.id === uid);
      return { shift_id: shift.id, user_id: uid, user_name: u?.name || '' };
    });
    if (staffRows.length) await supabase.from('shift_staff').insert(staffRows);

    onSaved();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 999, padding: '0 0 0' }}>
      <div style={{ ...S.card, width: '100%', maxWidth: 480, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, padding: '22px 20px 28px', animation: 'fadeUp .2s ease' }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.25rem', marginBottom: 18 }}>Buka Shift Baru</div>

        <div style={{ marginBottom: 14 }}>
          <div style={S.label}>Yang Membuka Shift</div>
          <select value={openedBy} onChange={e => setOpenedBy(e.target.value)} style={S.inp}>
            <option value=''>-- Pilih --</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={S.label}>Modal Kas Awal</div>
          <input type='number' placeholder='0' value={cash} onChange={e => setCash(e.target.value)} style={S.inp} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={S.label}>Staff yang Hadir</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {users.map(u => {
              const sel = staff.includes(u.id);
              return (
                <button key={u.id} onClick={() => toggleStaff(u.id)} style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${sel ? 'var(--gold)' : 'var(--border)'}`, background: sel ? '#FFF8E7' : 'white', color: sel ? 'var(--brown)' : 'var(--muted)', fontSize: '.78rem', fontWeight: sel ? 600 : 400 }}>
                  {u.name}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ ...S.btn(), background: 'white', color: 'var(--muted)', border: '1px solid var(--border)', flex: 1 }}>Batal</button>
          <button onClick={buka} disabled={saving} style={{ ...S.btn(), flex: 2, background: '#27AE60' }}>{saving ? 'Membuka...' : '✅ Buka Shift'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Tutup Shift ────────────────────────────────────────────────────────
function TutupShiftModal({ shift, users, onClose, onSaved }) {
  const [closedBy, setClosedBy] = useState('');
  const [cash, setCash]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [summary, setSummary]   = useState(null);

  useEffect(() => { loadSummary(); }, []);

  async function loadSummary() {
    const [ordersRes, txRes] = await Promise.all([
      supabase.from('pos_orders')
        .select('total,status,payment,discount_amount,loyalty_discount')
        .eq('shift_id', shift.id),
      supabase.from('transactions')
        .select('type,amount,category')
        .eq('shift_id', shift.id)
        .eq('is_cancelled', false),
    ]);
    const orders  = (ordersRes.data || []).filter(o => o.status === 'Selesai');
    const revenue = orders.reduce((s, o) => s + o.total, 0);
    const txs     = txRes.data || [];
    const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    const income  = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    setSummary({ orders: orders.length, revenue, expense, income });
  }

  async function tutup() {
    if (!closedBy) return alert('Pilih yang menutup shift');
    setSaving(true);
    const closer = users.find(u => u.id === closedBy);
    await supabase.from('shifts').update({
      closed_by: closedBy,
      closed_by_name: closer?.name || '',
      closing_cash: parseInt(cash) || 0,
      closed_at: new Date().toISOString(),
      status: 'closed',
    }).eq('id', shift.id);

    // Clockout semua staff yang belum clockout
    await supabase.from('shift_staff')
      .update({ clocked_out: new Date().toISOString() })
      .eq('shift_id', shift.id)
      .is('clocked_out', null);

    onSaved();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 999 }}>
      <div style={{ ...S.card, width: '100%', maxWidth: 480, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, padding: '22px 20px 28px', animation: 'fadeUp .2s ease' }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.25rem', marginBottom: 16 }}>Tutup Shift</div>

        {summary && (
          <div style={{ background: '#F9F6F1', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['Order Selesai', summary.orders + ' order'], ['Pendapatan', fmtRp(summary.revenue)], ['Pengeluaran', fmtRp(summary.expense)], ['Pemasukan Lain', fmtRp(summary.income)]].map(([k, v]) => (
              <div key={k}>
                <div style={S.label}>{k}</div>
                <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1rem' }}>{v}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={S.label}>Yang Menutup Shift</div>
          <select value={closedBy} onChange={e => setClosedBy(e.target.value)} style={S.inp}>
            <option value=''>-- Pilih --</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={S.label}>Kas Akhir (Hasil Hitung Fisik)</div>
          <input type='number' placeholder='0' value={cash} onChange={e => setCash(e.target.value)} style={S.inp} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ ...S.btn(), background: 'white', color: 'var(--muted)', border: '1px solid var(--border)', flex: 1 }}>Batal</button>
          <button onClick={tutup} disabled={saving} style={{ ...S.btn(), flex: 2, background: '#E53935' }}>{saving ? 'Menutup...' : '🔒 Tutup Shift'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Shift() {
  const [shifts, setShifts]   = useState([]);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [openShift, setOpenShift] = useState(null);   // shift yang sedang berjalan
  const [modal, setModal]     = useState(null);        // 'buka' | 'tutup'
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    const [sRes, uRes] = await Promise.all([
      supabase.from('shifts')
        .select('*, shift_staff(user_name, clocked_in, clocked_out)')
        .order('opened_at', { ascending: false })
        .limit(30),
      supabase.from('users').select('id,name,role').eq('is_active', true),
    ]);
    const list = sRes.data || [];
    setShifts(list);
    setUsers(uRes.data || []);
    setOpenShift(list.find(s => s.status === 'open') || null);
    setLoading(false);
  }

  const afterSave = () => { setModal(null); load(); };

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Shift</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{shifts.length} shift tercatat</div>
        </div>
        {openShift
          ? <button onClick={() => setModal('tutup')} style={{ ...S.btn('#E53935') }}>🔒 Tutup Shift</button>
          : <button onClick={() => setModal('buka')} style={{ ...S.btn('#27AE60') }}>➕ Buka Shift</button>
        }
      </div>

      {/* Status shift aktif */}
      {openShift && (
        <div style={{ ...S.card, padding: '16px 18px', marginBottom: 16, borderLeft: '4px solid #27AE60', background: '#F0FBF4' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: '#1B5E20', fontSize: '.9rem' }}>🟢 Shift Sedang Berjalan</div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '.65rem', color: '#388E3C' }}>{fmtDur(openShift.opened_at, null)}</div>
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--muted)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span>Dibuka: <strong style={{ color: 'var(--dark)' }}>{openShift.opened_by_name}</strong></span>
            <span>Mulai: <strong style={{ color: 'var(--dark)' }}>{fmtDT(openShift.opened_at)}</strong></span>
            <span>Kas Awal: <strong style={{ color: 'var(--dark)' }}>{fmtRp(openShift.opening_cash)}</strong></span>
          </div>
          {openShift.shift_staff?.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {openShift.shift_staff.map((s, i) => (
                <span key={i} style={{ padding: '3px 10px', borderRadius: 20, background: '#DCFCE7', color: '#166534', fontSize: '.72rem', fontFamily: "'DM Mono',monospace" }}>
                  {s.user_name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Daftar Shift */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Memuat...</div>
      ) : shifts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: '.84rem' }}>Belum ada shift. Buka shift pertama!</div>
      ) : shifts.map(sh => {
        const isOpen = sh.status === 'open';
        const isExp  = expanded === sh.id;
        const dur    = sh.closed_at ? fmtDur(sh.opened_at, sh.closed_at) : fmtDur(sh.opened_at, null);

        return (
          <div key={sh.id} style={{ ...S.card, marginBottom: 8, overflow: 'hidden' }}>
            <div style={{ padding: '13px 16px', display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(isExp ? null : sh.id)}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: isOpen ? '#27AE60' : '#9E9E9E', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                  <strong style={{ fontSize: '.88rem' }}>{fmtDT(sh.opened_at)}</strong>
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.6rem', padding: '1px 8px', borderRadius: 20, background: isOpen ? '#DCFCE7' : '#F5F5F5', color: isOpen ? '#166534' : '#757575' }}>
                    {isOpen ? 'BERJALAN' : 'TUTUP'}
                  </span>
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>
                  {sh.opened_by_name} · {dur}
                  {sh.shift_staff?.length > 0 && ` · ${sh.shift_staff.length} staff`}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '.95rem' }}>{fmtRp(sh.opening_cash)}</div>
                <div style={{ fontSize: '.65rem', color: 'var(--muted)' }}>kas awal</div>
              </div>
            </div>

            {isExp && (
              <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--border)' }}>
                <div style={{ paddingTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8, fontSize: '.78rem', marginBottom: 10 }}>
                  {[
                    ['Dibuka oleh', sh.opened_by_name],
                    ['Ditutup oleh', sh.closed_by_name || '-'],
                    ['Kas Awal',    fmtRp(sh.opening_cash)],
                    ['Kas Akhir',  sh.closing_cash ? fmtRp(sh.closing_cash) : '-'],
                    ['Waktu Buka',  fmtDT(sh.opened_at)],
                    ['Waktu Tutup', fmtDT(sh.closed_at)],
                    ['Durasi',      dur],
                  ].map(([k, v]) => (
                    <div key={k}><span style={{ color: 'var(--muted)' }}>{k}: </span><strong>{v}</strong></div>
                  ))}
                </div>
                {sh.shift_staff?.length > 0 && (
                  <div>
                    <div style={{ ...S.label, marginBottom: 6 }}>Staff Hadir</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {sh.shift_staff.map((s, i) => (
                        <span key={i} style={{ padding: '4px 12px', borderRadius: 20, background: '#F3F4F6', color: 'var(--dark)', fontSize: '.75rem' }}>
                          {s.user_name}
                          {s.clocked_out
                            ? <span style={{ color: 'var(--muted)', marginLeft: 4 }}>({fmtDur(s.clocked_in, s.clocked_out)})</span>
                            : <span style={{ color: '#27AE60', marginLeft: 4 }}>• aktif</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Modals */}
      {modal === 'buka' && <BukaShiftModal users={users} onClose={() => setModal(null)} onSaved={afterSave} />}
      {modal === 'tutup' && openShift && <TutupShiftModal shift={openShift} users={users} onClose={() => setModal(null)} onSaved={afterSave} />}
    </div>
  );
}
